import React from 'react';
import { AlertCircle, RotateCcw, Plane } from 'lucide-react';
import { Button } from '../ui/button';
import { aviationQuotes } from './pdfConstants';

const PDFPageDisplay = ({
  error, setError, loadPage, loadDocumentInfo,
  pageLoading, pageNumber, currentImageUrl,
  imageDimensions, scale, rotation, nightMode,
  imageKey, currentQuality, isSuperAdmin,
  loadingSeconds, currentQuote
}) => {
  const quote = aviationQuotes[currentQuote];

  return (
    <>
      {/* Error */}
      {error && (
        <div className="text-center p-8 max-w-md">
          <div className="bg-red-500/20 p-6 rounded-full w-fit mx-auto mb-6">
            <AlertCircle className="h-12 w-12 text-red-400" />
          </div>
          <h3 className="text-xl font-bold text-white mb-2">Error al cargar</h3>
          <p className="text-slate-400 mb-6">{error}</p>
          <div className="flex gap-3 justify-center">
            <Button 
              onClick={() => { setError(null); loadPage(pageNumber, 0); }}
              className="bg-teal-500 hover:bg-teal-600"
            >
              <RotateCcw className="h-4 w-4 mr-2" />
              Recargar pagina
            </Button>
            <Button 
              onClick={() => { setError(null); loadDocumentInfo(); }}
              variant="outline"
              className="border-slate-600 text-slate-300 hover:bg-slate-700"
            >
              Reiniciar todo
            </Button>
          </div>
        </div>
      )}

      {/* Carga de pagina con Skeleton */}
      {pageLoading && !error && (
        <div className="absolute inset-0 flex items-center justify-center bg-slate-900/90 z-20">
          <div className="absolute inset-0 flex items-center justify-center opacity-20">
            <div className="w-[500px] h-[650px] bg-slate-700 rounded-lg shadow-2xl animate-pulse">
              <div className="p-6 space-y-4">
                <div className="h-6 bg-slate-600 rounded w-3/4"></div>
                <div className="h-4 bg-slate-600 rounded w-1/2"></div>
                <div className="mt-8 space-y-3">
                  <div className="h-3 bg-slate-600 rounded w-full"></div>
                  <div className="h-3 bg-slate-600 rounded w-full"></div>
                  <div className="h-3 bg-slate-600 rounded w-5/6"></div>
                  <div className="h-3 bg-slate-600 rounded w-full"></div>
                  <div className="h-3 bg-slate-600 rounded w-4/5"></div>
                </div>
                <div className="mt-6 space-y-3">
                  <div className="h-3 bg-slate-600 rounded w-full"></div>
                  <div className="h-3 bg-slate-600 rounded w-3/4"></div>
                  <div className="h-3 bg-slate-600 rounded w-full"></div>
                </div>
              </div>
            </div>
          </div>
          
          <div className="text-center p-8 relative z-10">
            <div className="relative h-32 w-64 mx-auto mb-6">
              <div className="absolute top-1/2 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-teal-500/50 to-transparent"></div>
              <div 
                className="absolute top-1/2 -translate-y-1/2 transition-all duration-1000 ease-in-out"
                style={{ left: `${(loadingSeconds * 8) % 100}%` }}
              >
                <div className="relative">
                  <div className="absolute -inset-4 bg-teal-500/20 rounded-full blur-lg animate-pulse"></div>
                  <Plane className="h-10 w-10 text-teal-400 transform -rotate-12 relative" />
                </div>
              </div>
              <div 
                className="absolute top-1/2 h-1 bg-gradient-to-r from-teal-500/50 to-transparent rounded-full transition-all duration-500"
                style={{ 
                  left: '0%',
                  width: `${Math.min((loadingSeconds * 8) % 100, 40)}%`,
                  transform: 'translateY(-50%)'
                }}
              ></div>
            </div>
            
            <h3 className="text-xl font-semibold text-white mb-2">
              {loadingSeconds < 5 ? 'Preparando pagina...' :
               loadingSeconds < 15 ? 'Renderizando documento...' :
               loadingSeconds < 30 ? 'Procesando archivo grande...' :
               'Casi listo, por favor espere...'}
            </h3>
            
            <div className="w-64 h-1 bg-slate-700 rounded-full mx-auto overflow-hidden">
              <div className="h-full w-1/3 bg-gradient-to-r from-teal-500 to-cyan-400 rounded-full animate-loading-bar"></div>
            </div>
            
            {quote && (
              <div className="mt-6 px-8 max-w-md mx-auto">
                <p className="text-cyan-300 italic text-sm leading-relaxed">
                  "{quote.quote}"
                </p>
                <p className="text-slate-500 text-xs mt-2">
                  — {quote.author}
                </p>
              </div>
            )}
            
            <p className="text-slate-600 text-xs mt-4">{loadingSeconds}s</p>
          </div>
        </div>
      )}

      {/* Imagen de la pagina */}
      {!error && currentImageUrl && (
        <div>
          <img 
            key={`page-${pageNumber}-${imageKey}`}
            src={currentImageUrl} 
            alt={`Pagina ${pageNumber}`}
            data-testid="pdf-page-image"
            className={`shadow-2xl shadow-black/50 rounded-sm ${nightMode ? 'invert brightness-90 hue-rotate-180' : ''}`}
            style={{ 
              userSelect: 'none',
              WebkitUserSelect: 'none',
              pointerEvents: 'none',
              width: `${imageDimensions.width * scale}px`,
              height: 'auto',
              display: 'block',
              transform: rotation ? `rotate(${rotation}deg)` : undefined,
              transition: 'transform 0.3s ease',
            }}
            decoding="sync"
            draggable={false}
            onLoad={(e) => {
              const { naturalWidth, naturalHeight } = e.target;
              // Imagen cargada OK
            }}
            onError={(e) => {
              setError('Error mostrando pagina. Reintentando...');
              setTimeout(() => {
                setError(null);
                loadPage(pageNumber, 0);
              }, 1000);
            }}
          />
        </div>
      )}
    </>
  );
};

export default PDFPageDisplay;
